import React, { MouseEventHandler, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConnectionConfiguration, Destination, Member, Organization } from "../common/ObjectTypes";
import SchemaForm from "../common/SchemaForm";
import SelectInput from "../common/SelectInput";
import { ArrowDown, ArrowRight, AttentionIcon } from "../common/ProjectIcons";
import { checkSchema, isActionVisible } from "../common/SchemaFormTemplates/checkSchema";
import { isHiddenUrlDestinationType } from "./isHiddenUrlDestinationType";
import { isEarthRangerDestinationType } from "./earthRangerUrl";
import EarthRangerSiteInput from "./EarthRangerSiteInput";
import TestAction from "../common/TestAction";
import { buttonClasses } from "../common/tailwindClassnames";
import { useHelp, HelpCard } from "../../context/HelpContext";

type DestinationDetailsModalConfigProps = {
    currentMemberIsAdmin: boolean,
    destination: Destination,
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    onChange: Function,
    onChangeSelectedDestinationConfig: Function,
    onTestAction: Function,
    testActionLoading: boolean,
    testActionResponse: any,
    testActionStatus: number,
    onChangeSelectedDestinationOwner: Function,
    onSave: (e: React.FormEvent<Element>, action?: "save" | "saveAndClose") => void,
    onToggleModal: MouseEventHandler,
    changesMade: boolean,
    errors: any,
    rsjfErrors: any,
}

const DestinationDetailsModalConfig = ({
    currentMemberIsAdmin,
    destination,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    onChange,
    onChangeSelectedDestinationConfig,
    onTestAction,
    testActionLoading,
    testActionResponse,
    testActionStatus,
    onChangeSelectedDestinationOwner,
    onSave,
    changesMade,
    onToggleModal,
    errors,
    rsjfErrors,
}: DestinationDetailsModalConfigProps) => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();
    const [activeActionId, setActiveActionId] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const shouldHideUrlField = isHiddenUrlDestinationType(destination.type.name);
    const isEarthRanger = isEarthRangerDestinationType(destination.type.name);

    useEffect(() => {
        const cards: HelpCard[] = [
            {
                title: t('Setting up a Destination in Gundi'),
                description: t('Find step-by-step instructions for connecting your destination.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/connections/destinations' },
            },
            ...(destination.type.help_center_url ? [{
                title: t('{{technology}} Destination Set Up', { technology: destination.type.name }),
                description: t('Configuration guide specific to {{technology}} destinations.', { technology: destination.type.name }),
                button: { label: t('Learn More'), url: destination.type.help_center_url },
            }] : []),
        ];
        setHelpContent({ cards });
    }, [destination.type, i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);

    return (
        <div className="h-full flex flex-col bg-very-light-gray pb-16">
            <div className="flex flex-row w-full justify-center">
                <div className="w-full rounded-lg py-8 px-4">
                    <div className="rounded border border-field-outline bg-white p-[16px] transition-colors duration-300">
                        {/* Name */}
                        <div className="mb-[8px] border-b border-divider-lines pt-1 pb-[8px] transition-all duration-300 w-full">
                            <div className="flex flex-row items-start justify-between w-full gap-6">
                                <div className="w-1/2 flex flex-col">
                                    <label className="mr-1">{t("Name")} *</label>
                                    <span className="text-sm text-secondary-gray mt-1">{t("A descriptive name for this destination.")}</span>
                                </div>
                                <div className="w-1/2">
                                    <input
                                        id="name"
                                        placeholder=""
                                        className="w-full p-1.5 border border-neutral-gray-200 rounded rounded-sm px-2 transition-colors duration-300"
                                        value={destination.name || ""}
                                        onChange={(e) => onChange(e)}>
                                    </input>
                                    {errors.name && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.name}</p></div>}
                                </div>
                            </div>
                        </div>
                        {/* Workspace */}
                        <div className="mb-[8px] border-b border-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0 transition-all duration-300 w-full">
                            <div className="flex flex-row items-start justify-between w-full gap-6">
                                <div className="w-1/2 flex flex-col">
                                    <label className="mr-1">{t("Workspace")} *</label>
                                    <span className="text-sm text-secondary-gray mt-1">{t("The workspace this destination belongs to.")}</span>
                                </div>
                                <div className="w-1/2">
                                    <SelectInput
                                        options={organizations.map((org: Organization) => {
                                            return {
                                                label: org.name,
                                                value: org.id
                                            }
                                        })}
                                        className={"basic-single pb-1"}
                                        value={{ label: destination.owner.name, value: destination.owner.id }}
                                        autoSelectSingleOption={true}
                                        isDisabled={false}
                                        isLoading={false}
                                        isClearable={false}
                                        isSearchable={true}
                                        isMulti={false}
                                        name={t("Workspace")}
                                        onChange={(e: any) => onChangeSelectedDestinationOwner(e)}
                                        onMenuScrollToBottom={loadMoreOrgs}
                                        handleInputChange={orgInputChange}
                                    />
                                    {errors.owner && <div className="text-orange-600 bg-orange-100 py-1 px-2 rounded"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.owner}</p></div>}
                                </div>
                            </div>
                        </div>
                        {/* URL */}
                        {!shouldHideUrlField && (
                            <div className="mb-[8px] border-b border-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0 transition-all duration-300 w-full">
                                <div className="flex flex-row items-start justify-between w-full gap-6">
                                    <div className="w-1/2 flex flex-col">
                                        <label className="mr-1">{isEarthRanger ? t("Site URL") : t("URL")} *</label>
                                        <span className="text-sm text-secondary-gray mt-1">{isEarthRanger ? t("The name of the EarthRanger site (e.g. my-site).") : t("The base URL of the destination.")}</span>
                                    </div>
                                    <div className="w-1/2">
                                        {isEarthRanger ? (
                                            <EarthRangerSiteInput
                                                value={destination.base_url || ""}
                                                onChange={(e) => onChange(e)}
                                                error={errors.base_url}
                                            />
                                        ) : (
                                            <>
                                                <input
                                                    id="base_url"
                                                    placeholder=""
                                                    className="w-full p-1.5 border border-neutral-gray-200 rounded rounded-sm px-2 transition-colors duration-300"
                                                    value={destination.base_url || ""}
                                                    onChange={(e) => onChange(e)}>
                                                </input>
                                                {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex-grow pt-14">
                        {/* <label>Configurations</label> */}
                        {destination.type.actions.filter((item: any) => item.type !== "pull").map((item: any) => {
                            const uiSchema = item.ui_schema ? { ...item.ui_schema } : {};
                            const formTitle = item.ui_schema && item.ui_schema['ui:title'] ? item.ui_schema['ui:title'] : item.name;
                            const hasSchema = checkSchema(item);
                            const isVisible = isActionVisible(item);
                            return (
                                <div key={item.id} className={isVisible ? "pb-14" : "hidden"}>
                                    {isVisible && (
                                        <>
                                            <div className="mb-4 flex flex-col items-start w-full">
                                                {item.schema.hasOwnProperty('is_executable') && item.schema.is_executable === true ? (
                                                    <TestAction
                                                        actionId={item.id}
                                                        actionValue={item.value}
                                                        onTestAction={(id: any, value: any) => {
                                                            onTestAction(id, value);
                                                            setActiveActionId(id);
                                                        }}
                                                        isLoading={testActionLoading && activeActionId === item.id}
                                                        response={activeActionId === item.id ? testActionResponse : null}
                                                        formTitle={formTitle}
                                                        uiSchema={uiSchema}
                                                    />
                                                ) : (
                                                    <div className="text-2xl font-normal text-secondary-gray transition-all duration-300">{formTitle}</div>
                                                )}
                                                {uiSchema['ui:description'] && (
                                                    <div className="form-root-description !mb-0 text-gray-600 mt-1 transition-colors duration-300">
                                                        {uiSchema['ui:description']}
                                                    </div>
                                                )}
                                            </div>
                                            {item.schema?.properties && Object.keys(item.schema.properties).length > 0 && <div className="rounded border border-gray-lines bg-white p-5 mb-8 transition-colors duration-300">
                                                <SchemaForm
                                                    userIsAdmin={currentMemberIsAdmin}
                                                    formTitle={''}
                                                    schema={item.schema}
                                                    ui_schema={{...uiSchema, 'ui:description': ''}}
                                                    formdata={destination.configurations.findIndex((a: ConnectionConfiguration) => a.action.id === item.id) !== -1 ? destination.configurations.find((a: ConnectionConfiguration) => a.action.id === item.id).data : {}}
                                                    onChange={(e: any, fieldId?: string) => onChangeSelectedDestinationConfig(e, item.id, fieldId)}
                                                    isEditable={true}
                                                    errors={rsjfErrors[item.id]}
                                                />
                                            </div>}
                                        </>
                                    )}
                                </div>
                            )
                        })}
                    </div>

                    {/* advanced configurations */}
                    {shouldHideUrlField && <div className="pb-14">
                        <div className="mt-6 text-lg font-semibold text-secondary-gray transition-colors duration-300" onClick={() => setShowAdvanced(!showAdvanced)}>
                            {showAdvanced ? <ArrowDown /> : <ArrowRight />} {t("Advanced Configurations")}
                        </div>
                        {showAdvanced &&
                            <div className="ml-8 flex flex-col">
                                <label className="text-sm text-secondary-gray transition-colors duration-300">{t("URL")}</label>
                                <input
                                    id="base_url"
                                    placeholder=""
                                    className="mt-2 p-1.5 border border-neutral-gray-200 rounded rounded-sm px-2 transition-colors duration-300"
                                    value={destination.base_url || ""}
                                    onChange={(e) => onChange(e)}>
                                </input>
                                {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                            </div>}
                    </div>}
                </div>
            </div>
            {/* submit and cancel button */}
            <div className="fixed bottom-0 right-0 w-full py-3 bg-white/10 backdrop-blur shadow-lg transition-colors duration-300">
                <div className="max-w-list-table mx-auto flex flex-row gap-3 px-8">
                    <button className={buttonClasses.primary}
                        type="submit"
                        disabled={!changesMade}
                        onClick={(e: any) => onSave(e, "save")}>
                        {t("Save")}
                    </button>
                    <button className={buttonClasses.lightGreen}
                        type="submit"
                        disabled={!changesMade}
                        onClick={(e: any) => onSave(e, "saveAndClose")}>
                        {t("Save and Close")}
                    </button>
                    <button className={buttonClasses.cancel}
                        type="button"
                        onClick={onToggleModal}>
                        {t("Cancel")}
                    </button>
                </div>
            </div>
        </div>

    )
}

export default DestinationDetailsModalConfig;